import { Header } from "@/components/Header";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { BottomTabBar } from "@/components/BottomTabBar";
import { ChatWidget } from "@/components/ChatWidget";
import { SupportWidget } from "@/components/SupportWidget";
import { FloatingAd } from "@/components/FloatingAd";
import { AdSlot } from "@/components/AdSlot";
import { Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Seo } from "@/components/Seo";

export const Layout = ({ children, hideAd, seo }) => {
  const { t } = useI18n();
  return (
  <div className="min-h-screen flex flex-col bg-background pb-16">
    <Seo {...(seo || {})} />
    <ImpersonationBanner />
    <Header />
    <main className="flex-1">{children}</main>
    <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
      <AdSlot placement="footer" />
    </div>
    <footer className="mt-16 text-slate-300" style={{ backgroundColor: "#232F3E" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
        <div>
          <div className="font-display font-black text-lg mb-3 text-white">Xurray<span className="text-primary">Mart</span></div>
          <p className="text-slate-400 text-xs leading-relaxed">{t("footerTagline")}</p>
          <a href="mailto:contact@xurray.com" data-testid="footer-contact-email" className="inline-flex items-center gap-1.5 mt-3 text-xs text-slate-400 hover:text-primary transition-colors">
            <Mail className="w-3.5 h-3.5" /> contact@xurray.com
          </a>
        </div>
        <div>
          <h4 className="font-semibold mb-3 text-white">{t("shop")}</h4>
          <ul className="space-y-2 text-slate-400">
            <li><Link to="/products" className="hover:text-primary transition-colors">{t("allProducts")}</Link></li>
            <li><Link to="/products?flash=1" className="hover:text-primary transition-colors">{t("flashSales")}</Link></li>
            <li><Link to="/following" className="hover:text-primary transition-colors">Stores You Follow</Link></li>
            <li><Link to="/blog" className="hover:text-primary transition-colors">Xurray Journal</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3 text-white">{t("sell")}</h4>
          <ul className="space-y-2 text-slate-400">
            <li><Link to="/sell" className="hover:text-primary transition-colors">{t("becomeSeller")}</Link></li>
            <li><Link to="/seller" className="hover:text-primary transition-colors">{t("sellerDashboard")}</Link></li>
            <li><Link to="/partners" className="hover:text-primary transition-colors">Affiliate &amp; Influencer</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3 text-white">Help &amp; Support</h4>
          <ul className="space-y-2 text-slate-400">
            <li><Link to="/customer-service" className="hover:text-primary transition-colors">Customer Service</Link></li>
            <li><Link to="/disputes" className="hover:text-primary transition-colors">Disputes &amp; Reports</Link></li>
            <li><Link to="/return-refund-policy" className="hover:text-primary transition-colors">Return &amp; Refund Policy</Link></li>
            <li><Link to="/return-policy" className="hover:text-primary transition-colors">Return Policy</Link></li>
            <li><Link to="/offline-complaints" className="hover:text-primary transition-colors">Offline Complaints</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3 text-white">Trust &amp; Safety</h4>
          <ul className="space-y-2 text-slate-400">
            <li><Link to="/report-ip-violation" className="hover:text-primary transition-colors">Report an IP Violation</Link></li>
            <li><Link to="/dsa-osa" className="hover:text-primary transition-colors">DSA / OSA Information</Link></li>
            <li><Link to="/integrity-compliance" className="hover:text-primary transition-colors">Integrity &amp; Compliance</Link></li>
            <li><Link to="/transparency" className="hover:text-primary transition-colors">Transparency Center</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10" style={{ backgroundColor: "#1a2530" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p data-testid="vendor-content-disclaimer" className="text-[11px] leading-relaxed text-slate-400 text-center">
            Please note that all contents including images, products, pricing, promotions, brand ads and company information is the sole responsibility of the Vendor. xurray.com is not liable for such content. For any complaints or concerns, please contact{" "}
            <a href="mailto:contact@xurray.com" className="text-primary hover:underline">contact@xurray.com</a>.
          </p>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col items-center gap-2.5" data-testid="footer-payments">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">We accept</span>
          <div className="flex items-center gap-2.5 flex-wrap justify-center">
            <span className="bg-white rounded-md h-7 px-2.5 inline-flex items-center justify-center shadow-sm" title="Visa" data-testid="pay-visa">
              <span className="text-[#1A1F71] font-black italic text-[13px] tracking-tight">VISA</span>
            </span>
            <span className="bg-white rounded-md h-7 px-3 inline-flex items-center justify-center shadow-sm" title="Mastercard" data-testid="pay-mastercard">
              <span className="relative inline-flex items-center">
                <span className="block w-4 h-4 rounded-full bg-[#EB001B]" />
                <span className="block w-4 h-4 rounded-full bg-[#F79E1B] -ml-1.5 mix-blend-multiply" />
              </span>
            </span>
            <span className="bg-white rounded-md h-7 px-2.5 inline-flex items-center justify-center shadow-sm" title="PayPal" data-testid="pay-paypal">
              <span className="font-black italic text-[12px]"><span className="text-[#253B80]">Pay</span><span className="text-[#179BD7]">Pal</span></span>
            </span>
            <span className="bg-white rounded-md h-7 px-2.5 inline-flex items-center justify-center shadow-sm" title="Google Pay" data-testid="pay-googlepay">
              <span className="font-semibold text-[12px] tracking-tight">
                <span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">o</span><span className="text-[#FBBC05]">o</span><span className="text-[#4285F4]">g</span><span className="text-[#34A853]">l</span><span className="text-[#EA4335]">e</span>
              </span>
              <span className="text-[#5F6368] font-medium text-[12px] ml-1">Pay</span>
            </span>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10" style={{ backgroundColor: "#131921" }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>© 2026 Xurray Mart</span>
          <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/about" className="hover:text-primary transition-colors">About</Link>
            <Link to="/blog" className="hover:text-primary transition-colors">Journal</Link>
            <Link to="/contact" className="hover:text-primary transition-colors">Contact</Link>
            <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary transition-colors">Terms &amp; Conditions</Link>
          </nav>
        </div>
      </div>
    </footer>
    {!hideAd && <FloatingAd />}
    <ChatWidget />
    <SupportWidget />
    <BottomTabBar />
  </div>
  );
};
